import React from 'react';
import ScoreGauge from './ScoreGauge';
import CTAButton from './CTAButton';
import { generateResults } from '../utils/scoring';

/**
 * Results Component
 * Displays quiz results with scores, gauges, insights, and revenue leak estimate
 * 
 * @param {object} answers - All quiz answers
 * @param {object} leadData - Lead capture form data
 */
export default function Results({ answers, leadData }) {
  // Generate complete results using scoring utility
  const results = generateResults(answers);
  
  // Extract first name from full name
  const firstName = leadData.fullName.split(' ')[0];
  const companyName = leadData.companyName;
  
  // Zoho Bookings URL (from PRD)
  const bookingUrl = 'https://book.thryv.ing/#/4634692000002169048';
  
  return (
    <div className="min-h-screen relative z-10 py-4 sm:py-6 md:py-8 lg:py-12 px-3 sm:px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header with Logo */}
        <div className="text-center mb-4 sm:mb-6 md:mb-8">
          <img 
            src="/logos/thryv_consulting_logo_02tw_web.png" 
            alt="Thryv Consulting" 
            className="h-12 sm:h-16 md:h-20 mx-auto mb-2 sm:mb-4"
          />
        </div>
        
        {/* Main Results Card */}
        <div className="card">
          {/* Overall Score Section */}
          <div className="text-center mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-700">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 leading-tight">
              {companyName.toUpperCase()}'S<br />
              PIPELINE HEALTH SCORE
            </h1>
            
            {/* Overall Score Gauge */}
            <div className="max-w-md mx-auto mb-3 sm:mb-4">
              <div className="flex items-center justify-center gap-3 sm:gap-4 mb-2">
                <span className={`text-4xl sm:text-5xl font-bold ${results.overall.color.text}`}>
                  {results.overall.percentage}
                </span>
                <span className="text-2xl sm:text-3xl text-gray-400">/100</span>
              </div>
              
              {/* Overall Progress Bar */}
              <div className="w-full bg-gray-700 h-3 sm:h-4 rounded-full overflow-hidden mb-2">
                <div 
                  className={`h-full transition-all duration-800 ease-out`}
                  style={{ 
                    width: `${results.overall.percentage}%`,
                    backgroundColor: results.overall.color.color
                  }}
                />
              </div>
            </div>
            
            {/* Overall Insight */}
            <div className="max-w-2xl mx-auto">
              <h2 className={`text-base sm:text-lg md:text-xl font-semibold mb-1.5 sm:mb-2 ${results.overall.color.text}`}>
                {results.overall.insight.headline}
              </h2>
              <p className="text-sm sm:text-base text-gray-300">
                <span className="text-white font-semibold">{firstName}</span>, {results.overall.insight.message.charAt(0).toLowerCase() + results.overall.insight.message.slice(1)}
              </p>
            </div>
          </div>
          
          {/* Section Scores */}
          <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-700">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 md:mb-6 text-center">
              BREAKDOWN BY FUNNEL STAGE
            </h2>
            
            <div className="space-y-3 sm:space-y-4 md:space-y-6">
              {results.sections.map((section, index) => (
                <ScoreGauge
                  key={section.id}
                  label={section.name}
                  description={section.description}
                  percentage={section.percentage}
                  score={section.score}
                  maxScore={section.maxScore}
                  delay={index * 200} // Stagger animations
                />
              ))}
            </div>
          </div>
          
          {/* Revenue Leak Estimate */}
          <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-700 text-center">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4">
              ESTIMATED ANNUAL REVENUE LEAK
            </h2>
            
            <div className="bg-leak-red/10 border-2 border-leak-red/30 rounded-xl p-4 sm:p-5 md:p-6 mb-3 sm:mb-4">
              <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-leak-red mb-1.5 sm:mb-2">
                {results.revenueLeak.formatted}
              </p>
              <p className="text-xs sm:text-sm text-gray-400">
                Based on your revenue and pipeline gaps
              </p>
            </div>
          </div>
          
          {/* Specific Insights */}
          {results.specificInsights.length > 0 && (
            <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-700">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4">
                YOUR BIGGEST OPPORTUNITIES:
              </h2>
              
              <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
                {results.specificInsights.map((insight, index) => {
                  // Determine background and border colors based on severity
                  const isHighSeverity = insight.severity === 'high';
                  const bgColor = isHighSeverity ? 'bg-leak-red/10' : 'bg-caution-yellow/10';
                  const borderColor = isHighSeverity ? 'border-leak-red/30' : 'border-caution-yellow/30';
                  const iconColor = isHighSeverity ? 'text-leak-red' : 'text-caution-yellow';
                  
                  return (
                    <div 
                      key={index}
                      className={`flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border-2 ${bgColor} ${borderColor}`}
                    >
                      <span className={`flex-shrink-0 text-xl sm:text-2xl ${iconColor}`}>
                        {isHighSeverity ? '🔴' : '⚠️'}
                      </span>
                      <p className="text-xs sm:text-sm md:text-base text-white font-medium">
                        {insight.insight}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-navy-deep to-navy-dark rounded-xl p-4 sm:p-6 md:p-8 border border-gray-600/30">
              <CTAButton 
                leadData={leadData} 
                bookingUrl={bookingUrl}
                overallScore={results.overall.percentage}
              />
            </div>
          </div>
        </div>
        
        {/* Footer Note */}
        <div className="text-center mt-4 sm:mt-6 md:mt-8">
          <p className="text-gray-400 text-xs sm:text-sm">
            Results are based on your responses and industry benchmarks
          </p>
        </div>
      </div>
    </div>
  );
}

